import { rendererCapacity } from "./renderer-capacity";
const { maxNodes, maxConnections } = rendererCapacity;
export const vertexSource = `#version 300 es
in vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }`;

export const fragmentSource = `#version 300 es
precision highp float;
precision highp int;
out vec4 outColor;
uniform vec2 uResolution;
uniform sampler2D uNodes;
uniform sampler2D uConnections;
uniform vec2 uTileOrigin;
uniform vec2 uTileSize;
uniform vec2 uSampleSize;
uniform float uSpatialRange;
uniform vec2 uSpatialCenter;
uniform vec2 uSpatialScale;
uniform int uEnvironmentMode;
uniform int uEnvironmentProfile;
uniform int uEnvironmentSeed;
uniform int uNoiseOctaves;
uniform float uNoisePersistence;
uniform float uNoiseAmplitude;
uniform float uNoiseScale;
uniform vec2 uNoiseOffset;
uniform float uRepeatPeriod;
uniform int uRadialEnabled;
uniform int uFlipY;
uniform int uInputMappingKind;
uniform int uDiagnosticMode;
uniform vec2 uPhysicalTileSize;
uniform vec2 uBlockSize;
uniform int uSourcePass;
uniform int uChannelOrderIndex;
uniform int uInversionMask;
uniform float uRgbBlend;
uniform int uPaletteEnabled;
uniform int uPaletteCount;
uniform float uOutputScale;
uniform int uClampOutputs;
uniform int uPaletteSoftMax;
uniform int uLegacySoftMax;
uniform vec3 uPaletteColors[8];
uniform int uNodeCount;
uniform int uConnectionCount;
uniform int uInputIndices[6];
uniform int uOutputIndices[8];
float modPositive(float value, float divisor) { return value - divisor * floor(value / divisor); }
float rescale(float value) { return 2.0 * value - 1.0; }
uint noiseHash(ivec2 cell, int seed) {
  uint h = uint(seed);
  h ^= uint(cell.x) * 0x9E3779B9u;
  h = (h << 13u) | (h >> 19u);
  h ^= uint(cell.y) * 0x85EBCA6Bu;
  h ^= h >> 16u;
  h *= 0x7FEB352Du;
  h ^= h >> 15u;
  h *= 0x846CA68Bu;
  h ^= h >> 16u;
  return h;
}
float noiseHashValue(ivec2 cell, int seed) { return float(noiseHash(cell, seed)) / 4294967295.0 * 2.0 - 1.0; }
float valueNoise(vec2 point, int seed) {
  vec2 cell = floor(point);
  vec2 fraction = point - cell;
  vec2 smoothFraction = fraction * fraction * (3.0 - 2.0 * fraction);
  ivec2 base = ivec2(cell);
  float a = noiseHashValue(base, seed);
  float b = noiseHashValue(base + ivec2(1, 0), seed);
  float c = noiseHashValue(base + ivec2(0, 1), seed);
  float d = noiseHashValue(base + ivec2(1, 1), seed);
  return mix(mix(a, b, smoothFraction.x), mix(c, d, smoothFraction.x), smoothFraction.y);
}
float channelScale(int channel) {
  if (channel == 0) return 0.53;
  if (channel == 1) return 0.77;
  if (channel == 2) return 1.0;
  if (channel == 3) return 1.31;
  if (channel == 4) return 1.55;
  return 1.43;
}
vec2 tapestryOffset(int channel, int octave) {
  const vec2 offsets[24] = vec2[24](
    vec2(10.0, 20.0), vec2(-291.7, -391.2), vec2(891.7, 912.2), vec2(1293.0, 813.3),
    vec2(137.3, 113.8), vec2(-981.1, -531.8), vec2(-751.7, -863.2), vec2(682.8, -911.3),
    vec2(936.3, 256.4), vec2(-137.3, -631.7), vec2(-381.1, -812.2), vec2(-137.3, -631.7),
    vec2(324.2, 731.7), vec2(-547.5, -174.6), vec2(691.7, 731.2), vec2(-817.7, 1173.3),
    vec2(754.1, -591.2), vec2(354.5, 317.6), vec2(-469.6, -773.2), vec2(587.3, -517.1),
    vec2(-132.2, 173.7), vec2(-154.5, -117.6), vec2(169.7, 173.2), vec2(-187.4, 217.3)
  );
  return offsets[channel * 4 + octave];
}
vec2 patternOffset(int channel, int octave) { return vec2(17.3 * float(channel + 1) + 113.1 * float(octave), -29.7 * float(channel + 2) + 71.9 * float(octave)); }
float tapestryHash1(vec2 point) {
  uvec2 bits = floatBitsToUint(point);
  uint h = 0xc2b2ae3du * bits.x + 0x165667b9u;
  h = (h << 17u | h >> 15u) * 0x27d4eb2fu;
  h += 0xc2b2ae3du * bits.y;
  h = (h << 17u | h >> 15u) * 0x27d4eb2fu;
  h ^= h >> 15u;
  h *= 0x85ebca77u;
  h ^= h >> 13u;
  h *= 0xc2b2ae3du;
  h ^= h >> 16u;
  return uintBitsToFloat(h >> 9u | 0x3f800000u) - 1.0;
}
vec2 tapestryHash2(vec2 point) {
  float angle = 6.283185307 * tapestryHash1(point);
  return vec2(cos(angle), sin(angle));
}
vec2 diagnosticCoordinate;
vec2 diagnosticTransformedCoordinate;
float diagnosticHash1Value;
vec2 diagnosticHash2Value;
float diagnosticSimplexValue;
float diagnosticWeightedValue;
float diagnosticAccumulatedValue;
float diagnosticWeightValue;
float diagnosticFinalValue;
int diagnosticSelectedOctave;
int diagnosticSelectedCorner;
// Diagnostic modes 11-22 expose one exact scalar per pixel; normal modes 0-10 are unchanged.
float tapestrySimplexNoiseAt(vec2 point, int diagnosticOctave) {
  const float K1 = 0.366025404;
  const float K2 = 0.211324865;
  vec2 i = floor(point + (point.x + point.y) * K1);
  vec2 a = point - i + (i.x + i.y) * K2;
  float m = step(a.y, a.x);
  vec2 o = vec2(m, 1.0 - m);
  vec2 b = a - o + K2;
  vec2 c = a - 1.0 + 2.0 * K2;
  vec3 h = max(0.5 - vec3(dot(a, a), dot(b, b), dot(c, c)), 0.0);
  vec3 n;
  if (diagnosticOctave >= 0) {
    vec2 hash2A = tapestryHash2(i);
    vec2 hash2B = tapestryHash2(i + o);
    vec2 hash2C = tapestryHash2(i + 1.0);
    if (diagnosticOctave == diagnosticSelectedOctave) {
      if (diagnosticSelectedCorner == 0) { diagnosticHash1Value = tapestryHash1(i); diagnosticHash2Value = hash2A; }
      else if (diagnosticSelectedCorner == 1) { diagnosticHash1Value = tapestryHash1(i + o); diagnosticHash2Value = hash2B; }
      else { diagnosticHash1Value = tapestryHash1(i + 1.0); diagnosticHash2Value = hash2C; }
    }
    n = h * h * h * vec3(dot(a, hash2A), dot(b, hash2B), dot(c, hash2C));
  } else {
    n = h * h * h * vec3(
      dot(a, tapestryHash2(i)),
      dot(b, tapestryHash2(i + o)),
      dot(c, tapestryHash2(i + 1.0))
    );
  }
  return dot(n, vec3(32.99));
}
float tapestrySimplexNoise(vec2 point) { return tapestrySimplexNoiseAt(point, -1); }
float tapestryChannelNoise(int channel, vec2 point) {
  const float persistence = 0.25;
  const float noiseAmplitude = 1.2;
  float value = 0.0;
  float amplitude = 1.0;
  float frequency = 1.0;
  float weight = 0.0;
  bool diagnostic = uDiagnosticMode >= 11;
  if (diagnostic) diagnosticCoordinate = point;
  for (int octave = 0; octave < 4; octave++) {
    vec2 offset = tapestryOffset(channel, octave);
    if (!diagnostic) {
      value += tapestrySimplexNoise(offset + point * channelScale(channel) * frequency) * amplitude;
      weight += amplitude;
      amplitude *= persistence;
      frequency *= 2.0;
      continue;
    }
    vec2 transformed = offset + point * channelScale(channel) * frequency;
    float simplex = tapestrySimplexNoiseAt(transformed, diagnostic ? octave : -1);
    value += simplex * amplitude;
    weight += amplitude;
    if (diagnostic && octave == diagnosticSelectedOctave) {
      diagnosticTransformedCoordinate = transformed;
      diagnosticSimplexValue = simplex;
      diagnosticWeightedValue = simplex * amplitude;
      diagnosticAccumulatedValue = value;
      diagnosticWeightValue = weight;
    }
    amplitude *= persistence;
    frequency *= 2.0;
  }
  diagnosticFinalValue = noiseAmplitude * value / max(weight, 0.0001);
  return diagnosticFinalValue;
}
float environmentChannel(int channel, vec2 point) {
  if (uEnvironmentProfile == 1) {
    const float tapestryStateMapN4 = 1.0;
    const float tapestryStateMapN5 = 0.0;
    float noise = tapestryChannelNoise(channel, point);
    if (channel == 4) return tapestryStateMapN4 + 0.5 * noise;
    if (channel == 5) return tapestryStateMapN5 + 0.5 * noise;
    return noise;
  }
  float value = 0.0; float amplitude = 1.0; float frequency = 1.0; float weight = 0.0;
  for (int octave = 0; octave < 12; octave++) {
    if (octave >= uNoiseOctaves) break;
    vec2 offset = patternOffset(channel, octave);
    value += valueNoise(point * channelScale(channel) * uNoiseScale * frequency + offset, uEnvironmentSeed + channel * 1013 + octave * 7919) * amplitude;
    weight += amplitude; amplitude *= uNoisePersistence; frequency *= 2.0;
  }
  return uNoiseAmplitude * value / max(weight, 0.0001);
}
vec4 encodeDiagnosticFloat(float value) {
  uint bits = floatBitsToUint(value);
  return vec4(
    float((bits >> 24u) & 0xffu),
    float((bits >> 16u) & 0xffu),
    float((bits >> 8u) & 0xffu),
    float(bits & 0xffu)
  ) / 255.0;
}
int blockSampleIndex(int pixel, int physicalSize, int blockSize) {
  int remainder = physicalSize - (physicalSize / blockSize) * blockSize;
  if (remainder == 0) return pixel / blockSize;
  int left = remainder / 2;
  int interiorStart = left;
  int interiorEnd = left + (physicalSize / blockSize) * blockSize;
  if (pixel < interiorStart) return 0;
  if (pixel < interiorEnd) return (left > 0 ? 1 : 0) + (pixel - interiorStart) / blockSize;
  return physicalSize / blockSize + (left > 0 ? 1 : 0);
}
int getBlockStart(int sampleIndex, int physicalSize, int blockSize) {
  int remainder = physicalSize - (physicalSize / blockSize) * blockSize;
  if (remainder == 0) return sampleIndex * blockSize;
  int left = remainder / 2;
  int fullCount = physicalSize / blockSize;
  int firstInteriorIndex = left > 0 ? 1 : 0;
  if (sampleIndex < firstInteriorIndex) return 0;
  if (sampleIndex < firstInteriorIndex + fullCount) return left + (sampleIndex - firstInteriorIndex) * blockSize;
  return left + fullCount * blockSize;
}
int getBlockExtent(int sampleIndex, int physicalSize, int blockSize) {
  int start = getBlockStart(sampleIndex, physicalSize, blockSize);
  return min(blockSize, physicalSize - start);
}
float activate(int kind, float value) {
  if (kind == 0) return value;
  if (kind == 1) return sin(3.14159265 * value);
  if (kind == 2) return 2.0 * abs(modPositive(value, 2.0) - 1.0) - 1.0;
  if (kind == 3) return modPositive(value - 1.0, 2.0) - 1.0;
  if (kind == 4) return (modPositive(value - 1.0, 2.0) - 1.0) > 0.0 ? 1.0 : -1.0;
  if (kind == 5) return rescale(1.0 / (1.0 + exp(-value)));
  if (kind == 6) return rescale(exp(-pow(2.5 * value, 2.0)));
  return 2.0 * value * exp(0.5 - 2.0 * value * value);
}
vec3 reorderOutputs(vec3 values, int orderIndex) {
  if (orderIndex == 1) return values.xzy;
  if (orderIndex == 2) return values.yxz;
  if (orderIndex == 3) return values.yzx;
  if (orderIndex == 4) return values.zxy;
  if (orderIndex == 5) return values.zyx;
  return values.xyz;
}
void main() {
  // gl_FragCoord is at a pixel center. Keep its integer pixel index integer while mapping into the sample grid.
  // Reconstructing that index through normalized float coordinates can underflow before floor() at specific sizes.
  ivec2 tilePixel = ivec2(gl_FragCoord.xy) - ivec2(uTileOrigin);
  diagnosticSelectedOctave = uDiagnosticMode >= 15 && uDiagnosticMode <= 17 ? tilePixel.x / 3 : tilePixel.x;
  diagnosticSelectedCorner = uDiagnosticMode >= 15 && uDiagnosticMode <= 17 ? tilePixel.x - diagnosticSelectedOctave * 3 : 0;
  ivec2 physicalSize = ivec2(uPhysicalTileSize);
  ivec2 blockSize = ivec2(uBlockSize);
  ivec2 sampleIndex = uSourcePass == 1
    ? tilePixel
    : ivec2(blockSampleIndex(tilePixel.x, physicalSize.x, blockSize.x), blockSampleIndex(tilePixel.y, physicalSize.y, blockSize.y));
  vec2 blockStart = vec2(getBlockStart(sampleIndex.x, physicalSize.x, blockSize.x), getBlockStart(sampleIndex.y, physicalSize.y, blockSize.y));
  vec2 blockExtent = vec2(getBlockExtent(sampleIndex.x, physicalSize.x, blockSize.x), getBlockExtent(sampleIndex.y, physicalSize.y, blockSize.y));
  vec2 tileUv = (blockStart + blockExtent * 0.5) / uPhysicalTileSize;
  vec2 xy = (tileUv * 2.0 - 1.0) * uSpatialRange * uSpatialScale + uSpatialCenter;
  if (uFlipY == 1) xy.y = -xy.y;
  if (uDiagnosticMode == 1) { outColor = vec4(vec3(tileUv.x), 1.0); return; }
  if (uDiagnosticMode == 2) { outColor = vec4(vec3(tileUv.y), 1.0); return; }
  if (uDiagnosticMode == 3) { outColor = vec4(vec3((tileUv.x + tileUv.y) * 0.5), 1.0); return; }
  if (uDiagnosticMode == 4) { outColor = vec4(vec3(clamp(length(xy) / 1.41421356, 0.0, 1.0)), 1.0); return; }
  float inputs[6];
  if (uEnvironmentMode == 0) {
    inputs[0] = xy.x; inputs[1] = xy.y; inputs[2] = uRadialEnabled == 1 ? length(xy) / 1.41421356 : 0.0;
    inputs[3] = 0.0; inputs[4] = 1.0; inputs[5] = 0.0;
    if (uInputMappingKind == 1) {
      inputs[0] = sin(xy.x); inputs[1] = cos(xy.x); inputs[2] = sin(xy.y); inputs[3] = cos(xy.y); inputs[4] = 0.0; inputs[5] = 1.0;
    } else if (uInputMappingKind == 2) { inputs[4] = 0.0; inputs[5] = 1.0; }
  } else if (uEnvironmentMode == 1) {
    float repeatScale = 1.0 / max(uRepeatPeriod, 0.0001);
    float repeatTau = 6.283185307179586 * repeatScale;
    inputs[0] = cos(xy.x * repeatTau); inputs[1] = sin(xy.x * repeatTau);
    inputs[2] = cos(xy.y * repeatTau); inputs[3] = sin(xy.y * repeatTau);
    inputs[4] = 0.0; inputs[5] = 1.0;
  } else {
    vec2 environmentPoint = uEnvironmentProfile == 1 ? xy : xy + uNoiseOffset;
    if (uDiagnosticMode >= 11 && uEnvironmentProfile == 1) {
      inputs[1] = environmentChannel(1, environmentPoint);
      if (uDiagnosticMode == 11) { outColor = encodeDiagnosticFloat(diagnosticCoordinate.x); return; }
      if (uDiagnosticMode == 12) { outColor = encodeDiagnosticFloat(diagnosticCoordinate.y); return; }
      if (uDiagnosticMode == 13) { outColor = encodeDiagnosticFloat(diagnosticTransformedCoordinate.x); return; }
      if (uDiagnosticMode == 14) { outColor = encodeDiagnosticFloat(diagnosticTransformedCoordinate.y); return; }
      if (uDiagnosticMode == 15) { outColor = encodeDiagnosticFloat(diagnosticHash1Value); return; }
      if (uDiagnosticMode == 16) { outColor = encodeDiagnosticFloat(diagnosticHash2Value.x); return; }
      if (uDiagnosticMode == 17) { outColor = encodeDiagnosticFloat(diagnosticHash2Value.y); return; }
      if (uDiagnosticMode == 18) { outColor = encodeDiagnosticFloat(diagnosticSimplexValue); return; }
      if (uDiagnosticMode == 19) { outColor = encodeDiagnosticFloat(diagnosticWeightedValue); return; }
      if (uDiagnosticMode == 20) { outColor = encodeDiagnosticFloat(diagnosticAccumulatedValue); return; }
      if (uDiagnosticMode == 21) { outColor = encodeDiagnosticFloat(diagnosticWeightValue); return; }
      if (uDiagnosticMode == 22) { outColor = encodeDiagnosticFloat(diagnosticFinalValue); return; }
    } else {
      for (int channel = 0; channel < 6; channel++) inputs[channel] = environmentChannel(channel, environmentPoint);
    }
    if (uEnvironmentMode == 0 && uRadialEnabled == 0) inputs[2] = 0.0;
  }
  if (uDiagnosticMode >= 5 && uDiagnosticMode <= 10) { outColor = encodeDiagnosticFloat(inputs[uDiagnosticMode - 5]); return; }
  float values[${maxNodes}];
  for (int nodeIndex = 0; nodeIndex < ${maxNodes}; nodeIndex++) {
    if (nodeIndex >= uNodeCount) break;
    bool isInput = false; float inputValue = 0.0;
    for (int slot = 0; slot < 6; slot++) if (uInputIndices[slot] == nodeIndex) { isInput = true; inputValue = inputs[slot]; }
    if (isInput) { values[nodeIndex] = inputValue; continue; }
    vec4 node = texelFetch(uNodes, ivec2(nodeIndex, 0), 0);
    float sum = node.y; int first = int(node.z); int count = int(node.w);
    for (int offset = 0; offset < ${maxConnections}; offset++) {
      if (offset >= count || first + offset >= uConnectionCount) break;
      vec4 connection = texelFetch(uConnections, ivec2(first + offset, 0), 0);
      sum += values[int(connection.x)] * connection.y;
    }
    if (node.x >= 0.0) values[nodeIndex] = activate(int(node.x), sum);
    else {
      vec4 a = texelFetch(uNodes, ivec2(nodeIndex, 1), 0);
      vec4 b = texelFetch(uNodes, ivec2(nodeIndex, 2), 0);
      float mixed = 0.0;
      // Only prepared nonzero terms evaluate their basis function. No powers or normalization here.
      for (int kind = 0; kind < 8; kind++) {
        float weight = kind < 4 ? a[kind] : b[kind - 4];
        if (weight > 0.0) mixed += weight * activate(kind, sum);
      }
      values[nodeIndex] = mixed;
    }
  }
  if (uOutputScale == 0.0) { outColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
  if (uPaletteEnabled != 0) {
    float scores[8];
    float maximum = -32.0;
    for (int i = 0; i < 8; i++) {
      if (i >= uPaletteCount) break;
      float score = values[uOutputIndices[i]];
      if (isnan(score) || isinf(score)) score = 0.0;
      if (uClampOutputs != 0) score = max(score, 0.0);
      scores[i] = uPaletteSoftMax != 0 ? clamp(score, -32.0, 32.0) : score;
      maximum = max(maximum, scores[i]);
    }
    float total = 0.0;
    if (uPaletteSoftMax != 0) {
      for (int i = 0; i < 8; i++) {
        if (i >= uPaletteCount) break;
        scores[i] = exp(scores[i] - maximum);
        total += scores[i];
      }
    }
    vec3 color = vec3(0.0);
    for (int i = 0; i < 8; i++) {
      if (i >= uPaletteCount) break;
      float contribution = uPaletteSoftMax != 0 ? scores[i] / total : (uClampOutputs != 0 ? scores[i] : clamp(scores[i] * 0.5 + 0.5, 0.0, 1.0));
      color += contribution * uPaletteColors[i];
    }
    outColor = vec4(clamp(color * uOutputScale, 0.0, 1.0), 1.0);
    return;
  }
  vec3 ordered = reorderOutputs(vec3(values[uOutputIndices[0]], values[uOutputIndices[1]], values[uOutputIndices[2]]), uChannelOrderIndex);
  if ((uInversionMask & 1) != 0) ordered.x = -ordered.x;
  if ((uInversionMask & 2) != 0) ordered.y = -ordered.y;
  if ((uInversionMask & 4) != 0) ordered.z = -ordered.z;
  if (uClampOutputs != 0) {
    for (int i = 0; i < 3; i++) ordered[i] = isnan(ordered[i]) || isinf(ordered[i]) ? 0.0 : max(ordered[i], 0.0);
  }
  vec3 weights = uClampOutputs != 0 ? ordered : clamp(ordered * 0.5 + 0.5, 0.0, 1.0);
  if (uLegacySoftMax != 0) {
    for (int i = 0; i < 3; i++) if (isnan(ordered[i]) || isinf(ordered[i])) ordered[i] = 0.0;
    ordered = clamp(ordered, -32.0, 32.0);
    weights = exp(ordered - max(ordered.x, max(ordered.y, ordered.z)));
    weights /= weights.x + weights.y + weights.z;
  }
  vec3 basis1 = mix(vec3(0.53, 0.32, 0.15), vec3(1.0, 0.0, 0.0), uRgbBlend);
  vec3 basis2 = mix(vec3(0.30, 0.44, 0.26), vec3(0.0, 1.0, 0.0), uRgbBlend);
  vec3 basis3 = mix(vec3(0.21, 0.31, 0.48), vec3(0.0, 0.0, 1.0), uRgbBlend);
  vec3 color = clamp((weights.x * basis1 + weights.y * basis2 + weights.z * basis3) * uOutputScale, 0.0, 1.0);
  outColor = vec4(color, 1.0);
}`;
export const blitVertexSource = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
void main() { v_uv = a_position * 0.5 + 0.5; gl_Position = vec4(a_position, 0.0, 1.0); }`;
export const blitFragmentSource = `#version 300 es
precision highp float;
precision highp int;
uniform sampler2D uPattern;
uniform vec2 uTileOrigin;
uniform vec2 uTileSize;
uniform vec2 uSampleSize;
uniform vec2 uBlockSize;
out vec4 outColor;
int blockSampleIndex(int pixel, int physicalSize, int blockSize) {
  int remainder = physicalSize - (physicalSize / blockSize) * blockSize;
  if (remainder == 0) return pixel / blockSize;
  int left = remainder / 2;
  int interiorStart = left;
  int interiorEnd = left + (physicalSize / blockSize) * blockSize;
  if (pixel < interiorStart) return 0;
  if (pixel < interiorEnd) return (left > 0 ? 1 : 0) + (pixel - interiorStart) / blockSize;
  return physicalSize / blockSize + (left > 0 ? 1 : 0);
}
void main() {
  ivec2 tilePixel = ivec2(gl_FragCoord.xy) - ivec2(uTileOrigin);
  ivec2 sampleIndex = ivec2(blockSampleIndex(tilePixel.x, int(uTileSize.x), int(uBlockSize.x)), blockSampleIndex(tilePixel.y, int(uTileSize.y), int(uBlockSize.y)));
  outColor = texelFetch(uPattern, sampleIndex, 0);
}`;
// Diagnostic-only capture of the production shader's 1x1 pixel-to-sample calculation.
export const coordinateDiagnosticFragmentSource = `#version 300 es
precision highp float;
uniform vec2 uTileSize;
uniform vec2 uSampleSize;
out vec4 outColor;
void main() {
  ivec2 tilePixel = ivec2(gl_FragCoord.xy);
  ivec2 sampleIndex = (tilePixel * ivec2(uSampleSize)) / ivec2(uTileSize);
  vec2 tileUv = (vec2(sampleIndex) + vec2(0.5)) / uSampleSize;
  outColor = vec4(tileUv, 0.0, 1.0);
}`;
